import React, { useContext, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import BorderlessButton from '../../../../components/common/forms/Button/BorderlessButton';
import { Colors } from '../../../../scss/common/style';
import DeleteComponent from '../../../../components/common/DeleteComponent';
import InventoryApi from '../../../../services/api/InventoryApi';
import MovementBox from './MovementBox';
import {
  DATE_FORMAT,
  WAREHOUSE_MOVEMENT_CONDITION_TYPES,
  STATUS_COLORS,
  ROLES,
  DATE_FORMAT_WITH_TIME,
  DATE_FORMAT_WITH_TIME_WITH_FRACTION_SECONDS,
} from '../../../../utils/constants';
import { StockTransactionGetDto, toGlobalUtc, toLocalEquivalentUtcDate } from 'svila-common';
import VerticalValueLabel from './VerticalValueLabel';
import { AxiosResponse } from 'axios';
import MyContext from '~/src/services/context';
import RolesDependent from '~/src/components/common/RolesDependent';

import sliderHorizontalIcon from '../../../../static/img/slidersHorizontal.svg';
import editIcon from '../../../../static/img/editIcon.svg';
import deleteIcon from '../../../../static/img/deleteIcon.svg';

const handleColor = (type: string): string => {
  if (type === 'Add' || type === 'EditAdd') {
    return Colors.nGreen;
  } else if (type === 'Remove' || type === 'EditRemove') {
    return Colors.nRed;
  } else if (type === 'Adjust' || type === 'EditAdjust') {
    return Colors.newOrage;
  } else if (type === 'Change') {
    return Colors.newOrage;
  } else if (type === WAREHOUSE_MOVEMENT_CONDITION_TYPES.MISSING.value) {
    return STATUS_COLORS.MISSING;
  } else {
    return Colors.veryLightBlue;
  }
};

interface Props {
  item: StockTransactionGetDto;
  key: string;
  updateMovemnt: boolean;
  setUpdateMovemnt: any;
}

export default function MovementComponent({ item, key, updateMovemnt, setUpdateMovemnt }: Props): JSX.Element {
  const history = useHistory();
  const [copied, setCopied] = useState(false);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
  };

  const deleteItem = async (transactionId: string): Promise<AxiosResponse<StockTransactionGetDto>> => {
    const result = await InventoryApi.delete(transactionId);
    return result;
  };

  const getMovementDate = (item: StockTransactionGetDto): string => {
    if (
      item.transactionType === 'EditAdd' ||
      item.transactionType === 'EditAdjust' ||
      item.transactionType === 'EditDelete'
    ) {
      return moment(item.relatedTransaction.transactionDateTime).format(DATE_FORMAT);
    } else {
      return moment(item.transactionDateTime).format(DATE_FORMAT);
    }
  };

  return (
    <RolesDependent roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}>
      <StyledRow
        key={key}
        status={
          item.condition === WAREHOUSE_MOVEMENT_CONDITION_TYPES.MISSING.value
            ? item.condition
            : item.transactionType
        }
        isRelated={item.relatedTransaction?.id && item.transactionType !== 'Remove' ? true : false}
      >
        <MovementBox movementDate={getMovementDate(item)} item={item}>
          <StyledCol>
            <>
              <div>
                <TitleId>id:</TitleId> <Id>{item.id?.slice(-6)}</Id>
                <TitleId className="related" onClick={() => handleCopy(item.relatedTransaction?.id)}>
                  related:
                </TitleId>
                <Id>
                  {' '}
                  {item.relatedTransaction?.id?.slice(-6)}{' '}
                  <CoppiedId>{copied && <span style={{ color: 'green' }}>Copied!</span>}</CoppiedId>
                </Id>
              </div>
              <VerticalValueLabel value={item.transactionType || '--'} label="Transaction" />
              {(item.transactionType === 'Add' ||
                item.transactionType === 'EditAdd' ||
                item.transactionType === 'Adjust' ||
                item.transactionType === 'EditAdjust') && (
                  <BorderlessButton
                    title="Edit"
                    icon={sliderHorizontalIcon}
                    onClick={(): void => {
                      history.push({
                        pathname: `/inventory/edit/${item.product.genericProductId}`,
                        state: { item },
                      });
                    }}
                    vertical
                  />
                )}
              {(item.transactionType === 'Remove' || item.transactionType === 'EditRemove') && (
                <BorderlessButton
                  title="Edit"
                  icon={editIcon}
                  onClick={(): void => {
                    history.push({
                      pathname: `/inventory/edit-remove`,
                      state: { item },
                    });
                  }}
                  vertical
                />
              )}
              <DeleteComponent
                title="Delete"
                icon={deleteIcon}
                id={item.id}
                onDelete={deleteItem}
                updateMovemnt={updateMovemnt}
                setUpdateMovemnt={setUpdateMovemnt}
              />
            </>
            <div>
              <ProductName>{`${item?.user?.firstName} ${item?.user?.lastName} `}</ProductName>
              <Data>{`${moment(item.transactionDateTime).format(
                DATE_FORMAT_WITH_TIME_WITH_FRACTION_SECONDS,
              )}`}</Data>
            </div>
            {item.notes && <VerticalValueLabel value={'Notes'} label={item.notes} />}
          </StyledCol>
        </MovementBox>
      </StyledRow>
    </RolesDependent>
  );
}

const StyledRow = styled.div`
  position: relative;
  display: flex;
  margin: 16px;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  border-radius: 2px solid #eeeeee;
  &:after {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    content: '';
    width: 33px;
    background: ${(props): void => {
    return handleColor(props.status);
  }};
  }
`;

const Data = styled.span`
  font-size: 14px;
  color: #343434;
  font-weight: 700;
  line-height: 23px;
  padding: 0 10px;
  text-align: center;
`;

const ProductName = styled.span`
  font-size: 16px;
  color: #343434;
  font-weight: 700;
  line-height: 23px;
  padding: 0 10px;
  text-align: center;
`;

const StyledCol = styled.div`
  height: 100%;
  padding: 10px 0 !important;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  background: #fbfbfb;

  div {
    display: flex;
    justify-content: center;
  }
`;

const TitleId = styled.span`
  font-size: 16px;
  color: #343434;
  font-weight: 700;
  line-height: 23px;
  padding: 0 5px;
  text-align: center;
`;

const Id = styled.span`
  position: relative;
  font-size: 14px;
  color: #343434;
  font-weight: 700;
  line-height: 23px;
  padding: 0 5px;
  text-align: center;
`;

const CoppiedId = styled.span`
  color: green;
  position: absolute;
  bottom: -15px;
  right: 5px;
`;
